import * as React from 'react';
import queryString from 'query-string';
import { autorun, configure } from 'mobx';
//import { setLogEnabled } from 'mobx-react-devtools';

import { ModernizrDetect } from './domains/layouts/state/environmentState/modernizr/ModernizrBrowser';
import { setCookie, getCookie } from './domains/layouts/config/config';
import { Session } from 'src_common/sdk/session';
import { getEnvVariables } from './utils/getEnvVariables';
import { initParamNewDesign } from 'src/utils/new-design';
import { TrpcClient } from './appState/TrpcClient';
import { getUniverseModule } from './domains/common/universes';

import { Common } from './domains/common/Common';
import { BasicDataModel } from './domains/players/state/BasicDataModel';
import { FullStoryState } from './domains/layouts/state/fullStory/FullStoryState';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { UserAttributionState } from 'src/domains/layouts/state/userAttributionState/userAttributionState';
import { GoogleTagManagerState } from './domains/layouts/state/googleState/GoogleTagManagerState';

import { initWebsocketV1 } from './index.init.websocketV1';
import { starAppStateInit } from './universes/star/AppState/starAppStateInit'; // TODO reduce blocker IsStaticState in AppLayoutsState - move to status function

export const getElement = (id: string): HTMLElement => {
    const node = document.getElementById(id);
    if (node !== null) {
        return node;
    }

    const newNode = document.createElement('div');
    newNode.setAttribute('id', id);
    document.body.appendChild(newNode);
    return newNode;
};

export const initIndex = async (): Promise<Common> => {
    //https://mobx.js.org/refguide/api.html -> isolateGlobalState
    configure({ isolateGlobalState: true });

    // Temporary solution to show clients new design system implementation. Remove it once the new design is released.
    initParamNewDesign();

    //setLogEnabled(true);

    React.Component.prototype.componentDidCatch = (error: unknown, errorInfo: unknown): void => {
        console.error('Default error componentDidCatch', error, errorInfo);
    };

    const envVariables = await getEnvVariables();

    if (envVariables === null) {
        throw Error('getEnvVariables: Failed to retrieve environment variables');
    }

    const websiteBaseUrl = window.location.origin;
    const isMobileAppReview: boolean = envVariables.mobileWrapperIsInReview === 'app-in-review';
    // Create application instance
    const session = await Session.start({
        websocket_api_host: envVariables.websocket_api_host,
        websocketUrl: envVariables.websocket_host_v2,
        apiTimeout: 60 * 1000,
        startPing: true,
        cacheApiCall: envVariables.cacheApiCall,
        getInitJwt: (): string | null => {
            const value = getCookie('website.sid');
            return value;
        },
        setJwt: (newJwt: string): void => {
            setCookie('website.sid', newJwt, 120);
        },
        mode: 'browser',
    });
    const startingUrl = location.pathname + location.search;
    const universeModule = await getUniverseModule(envVariables.universe, envVariables.isProdMode);
    const faviconUrl = universeModule.favicon;
    const isBrowser = true;
    const common = new Common(
        startingUrl,
        envVariables,
        session,
        isBrowser,
        new TrpcClient(websiteBaseUrl, isBrowser, session, isMobileAppReview),
        universeModule.themeColors,
        envVariables.features,
        faviconUrl,
        ModernizrDetect,
        websiteBaseUrl
    );

    starAppStateInit(common);

    // Fire login tag once when restored session yields basic data

    const gtm = GoogleTagManagerState.get(common);
    const dispose = autorun(() => {
        const id = BasicDataModel.get(common).basicDataReady?.id ?? null;
        if (id !== null && common.session.isAuthorized === true) {
            gtm.loginTag(id);
            dispose();
        }
    });

    window.addEventListener('click', () => {
        UserAttributionState.get(common).userAttributionsLocalStorage.validateDate();
    });

    const urlParams = queryString.parse(window.location.search);

    //Set cookie from url
    for (const cookieName of ['btag', 'stag', 'referrer', 'cxd']) {
        const valueFromUrl = urlParams[cookieName];

        if (typeof valueFromUrl === 'string') {
            setCookie(cookieName, valueFromUrl, 31, 'None');
        }

        if (Array.isArray(valueFromUrl)) {
            const firstValue = valueFromUrl[0];
            if (firstValue !== undefined) {
                setCookie(cookieName, firstValue, 31, 'None');
            }
        }
    }

    initWebsocketV1(common);

    window.onpopstate = (): void => {
        StarRouter.get(common).setUrl(location.pathname + location.search);
    };

    autorun(() => {
        const { url } = StarRouter.get(common);
        const { pathname, search } = window.location;

        if (`${pathname}${search}` !== url) {
            window.history.pushState(null, '', url);
        }
    });

    // init fullstory in constructor
    FullStoryState.get(common).runOnStart();

    return common;
};
